import { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import logo from '@/assets/svg/logo.svg';
import PortalNavigation from '@/components/PortalNavigation/PortalNavigation';
import { keycloakLogout } from '@/config/keycloak';
import useUserProfile from '@/hooks/useKeycloakUser';
import { GroupsEnum } from '@/types/users';

const Layout = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [desktopMenuOpen, setDesktopMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const { keycloak, user } = useUserProfile();
  const { pathname } = useLocation();
  const normalizedGroups = user?.groups?.map((group) => group.trim().toLocaleLowerCase('ru-RU'));
  const hasGroup = (group: GroupsEnum) =>
    normalizedGroups?.includes(group.toLocaleLowerCase('ru-RU')) ?? false;
  const isRecruiter = hasGroup(GroupsEnum.RECRUTER);
  const canViewExternalLink = hasGroup(GroupsEnum.EXTERNAL_LINK);
  const pageTitle = pathname === '/candidates' ? 'Кандидаты' : 'Главная';

  const displayName =
    user?.firstname && user?.secondname
      ? `${user.firstname} ${user.secondname}`
      : user?.preferred_username || user?.name || 'Пользователь';
  const initials =
    `${user?.firstname?.charAt(0)?.toUpperCase() || ''}${user?.secondname?.charAt(0)?.toUpperCase() || ''}` ||
    user?.preferred_username?.charAt(0)?.toUpperCase() ||
    user?.name?.charAt(0)?.toUpperCase() ||
    'П';

  const closeUserMenu = () => {
    setShowConfirm(true);
    setUserMenuOpen(false);
  };

  const navigation = (onNavigate?: () => void) => (
    <PortalNavigation
      isRecruiter={isRecruiter}
      canViewExternalLink={canViewExternalLink}
      onNavigate={onNavigate}
    />
  );

  return (
    <div className="relative flex min-h-screen flex-col bg-gray-50 md:flex-row">
      {menuOpen && (
        <button
          type="button"
          className="fixed inset-0 z-40 cursor-default bg-black bg-opacity-30 md:hidden"
          onClick={() => setMenuOpen(false)}
          aria-label="Закрыть меню"
        />
      )}

      <aside
        className={`fixed left-0 top-0 z-50 flex h-full w-72 flex-col overflow-x-hidden border-r border-gray-200 bg-white pt-6 transition-transform duration-200 md:hidden ${menuOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <button
          type="button"
          className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded transition hover:bg-gray-200"
          onClick={() => setMenuOpen(false)}
          aria-label="Закрыть меню"
        >
          <svg width="24" height="24" fill="none" viewBox="0 0 14 14" aria-hidden="true">
            <path
              fill="#888"
              d="M.65.65a.5.5 0 0 1 .7 0L7 6.29 12.65.65a.5.5 0 1 1 .7.7L7.71 7l5.64 5.65a.5.5 0 0 1-.7.7L7 7.71l-5.65 5.64a.5.5 0 0 1-.7-.7L6.29 7 .65 1.35a.5.5 0 0 1 0-.7Z"
            />
          </svg>
        </button>
        <div className="mx-auto mb-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg">
          <img src={logo} alt="Familia" className="h-full w-full object-contain" />
        </div>
        {navigation(() => setMenuOpen(false))}
        <div className="w-full shrink-0 pb-4 text-center text-xs text-gray-400">© Familia</div>
      </aside>

      <main className="flex min-w-0 flex-1 flex-col overflow-hidden pt-0">
        <header className="flex w-full flex-row items-center justify-between gap-3 bg-white px-4 py-3 shadow md:px-8">
          <button
            type="button"
            className="flex h-10 w-10 items-center justify-start rounded transition hover:bg-gray-200 md:hidden"
            onClick={() => setMenuOpen(true)}
            aria-label="Открыть меню"
          >
            <svg width="28" height="28" fill="none" viewBox="0 0 24 24" aria-hidden="true">
              <rect y="5" width="24" height="2" rx="1" fill="#888" />
              <rect y="11" width="24" height="2" rx="1" fill="#888" />
              <rect y="17" width="24" height="2" rx="1" fill="#888" />
            </svg>
          </button>
          <button
            type="button"
            className="hidden h-10 w-10 items-center justify-center rounded transition hover:bg-gray-200 md:flex"
            onClick={() => setDesktopMenuOpen((open) => !open)}
            aria-label={desktopMenuOpen ? 'Закрыть меню' : 'Открыть меню'}
          >
            {desktopMenuOpen ? (
              <svg width="28" height="28" fill="none" viewBox="0 0 14 14" aria-hidden="true">
                <path
                  fill="#888"
                  d="M.65.65a.5.5 0 0 1 .7 0L7 6.29 12.65.65a.5.5 0 1 1 .7.7L7.71 7l5.64 5.65a.5.5 0 0 1-.7.7L7 7.71l-5.65 5.64a.5.5 0 0 1-.7-.7L6.29 7 .65 1.35a.5.5 0 0 1 0-.7Z"
                />
              </svg>
            ) : (
              <svg width="28" height="28" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                <rect y="5" width="24" height="2" rx="1" fill="#888" />
                <rect y="11" width="24" height="2" rx="1" fill="#888" />
                <rect y="17" width="24" height="2" rx="1" fill="#888" />
              </svg>
            )}
          </button>
          <h1 className="flex-1 text-left text-sm font-medium md:text-2xl">{pageTitle}</h1>
          <button
            type="button"
            className="relative flex cursor-pointer items-center gap-2 border-none bg-transparent"
            onClick={() => setUserMenuOpen((open) => !open)}
            aria-haspopup="true"
            aria-expanded={userMenuOpen}
          >
            <div className="hidden flex-col items-end sm:flex">
              <span className="text-right text-sm font-medium text-gray-800 md:text-base">
                {displayName}
              </span>
              <span className="text-xs text-gray-500">{user?.email || 'email не указан'}</span>
            </div>
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-black text-lg text-white">
              {initials}
            </div>
            {userMenuOpen && (
              <div className="absolute right-0 top-full z-50 mt-2 flex min-w-[120px] flex-col rounded-lg border bg-white py-2 shadow-lg">
                <button
                  type="button"
                  className="cursor-pointer px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                  onClick={closeUserMenu}
                >
                  Выход
                </button>
              </div>
            )}
          </button>
        </header>

        <div className="flex h-auto min-h-0 min-w-0 md:h-[calc(100dvh-64px)]">
          <aside
            className={`hidden h-full shrink-0 flex-col overflow-x-hidden border-r bg-white pt-6 transition-all duration-200 md:flex ${desktopMenuOpen ? 'w-72' : 'w-0'}`}
          >
            <div className="mx-auto mb-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg">
              <img src={logo} alt="Familia" className="h-full w-full object-contain" />
            </div>
            {navigation()}
            <span className="mt-auto w-full shrink-0 text-center text-xs text-gray-400">
              © Familia
            </span>
          </aside>
          <div className="h-full min-w-0 max-w-full flex-1 overflow-x-hidden overflow-y-auto p-4">
            <Outlet />
          </div>
        </div>
      </main>

      {showConfirm && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="w-full max-w-xs rounded-lg bg-white p-6 shadow-lg">
            <div className="mb-4 text-lg font-semibold text-gray-900">
              Вы уверены, что хотите выйти?
            </div>
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                className="rounded bg-gray-200 px-4 py-2 text-gray-800 hover:bg-gray-300"
                onClick={() => setShowConfirm(false)}
              >
                Нет
              </button>
              <button
                type="button"
                className="rounded bg-[#631d76] px-4 py-2 text-white hover:bg-[#674d81]"
                onClick={() => keycloakLogout(keycloak)}
              >
                Да
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;
